import { Check, Copy01 } from '@untitledui/icons';
import { useCallback } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { Badge } from '@/lib/base/badges/badges';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { Toggle } from '@/lib/base/forms/toggle';
import type { CrmObject } from '@/types/crm';

import { buildWebhookPublicUrl } from './inbound-webhook-service';

export interface WebhookListItemProps {
  webhook: CrmObject;
  onToggle: (entityId: string, enabled: string) => void;
  onRowClick: (entityId: string) => void;
  isToggling?: boolean;
  hasReceivedTraffic?: boolean;
  showCreateAutomationCta?: boolean;
  onCreateAutomation?: (entityId: string, displayName: string) => void;
}

export function WebhookListItem({
  webhook,
  onToggle,
  onRowClick,
  isToggling,
  hasReceivedTraffic,
  showCreateAutomationCta,
  onCreateAutomation,
}: WebhookListItemProps) {
  const name =
    (webhook.properties?.['ahoy_display_name']?.value as string | undefined) ??
    'Untitled webhook';
  const enabledRaw = webhook.properties?.['ahoy_webhook_enabled']?.value as
    | string
    | undefined;
  const enabled = enabledRaw?.trim().toLowerCase() !== 'false';
  const slug = webhook.properties?.['ahoy_webhook_slug']?.value as
    | string
    | undefined;
  const url = slug ? buildWebhookPublicUrl(slug) : null;

  const { copied, copy } = useClipboard();

  const handleAction = useCallback(() => {
    onRowClick(webhook.entity_id);
  }, [webhook.entity_id, onRowClick]);

  const handleToggle = useCallback(
    (isSelected: boolean) => {
      onToggle(webhook.entity_id, isSelected ? 'true' : 'false');
    },
    [webhook.entity_id, onToggle]
  );

  const handleCopy = useCallback(() => {
    if (url) {
      void copy(url, 'url');
    }
  }, [copy, url]);

  const handleCreateAutomation = useCallback(() => {
    onCreateAutomation?.(webhook.entity_id, name);
  }, [onCreateAutomation, webhook.entity_id, name]);

  const isCopied = copied === 'url';

  const trailing = (
    <div className='flex items-center gap-2'>
      {showCreateAutomationCta && (
        <Button
          size='sm'
          color='secondary'
          onClick={handleCreateAutomation}
          aria-label={`Create automation for ${name}`}
        >
          Create automation
        </Button>
      )}
      <Button
        color='secondary'
        size='sm'
        iconLeading={isCopied ? Check : Copy01}
        onClick={handleCopy}
        isDisabled={!url}
        aria-label={isCopied ? 'Webhook URL copied' : 'Copy webhook URL'}
      >
        {isCopied ? 'Copied' : 'Copy'}
      </Button>
      <Toggle
        isSelected={enabled}
        isDisabled={isToggling}
        aria-label={enabled ? 'Disable webhook' : 'Enable webhook'}
        onChange={handleToggle}
      />
    </div>
  );

  return (
    <ButtonList.Item
      onAction={handleAction}
      aria-label={`Open ${name} details${hasReceivedTraffic ? ', has received traffic' : ''}`}
      trailing={trailing}
      showChevron
    >
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium text-fg-primary'>{name}</span>
        {hasReceivedTraffic && (
          <Badge size='sm' color='gray'>
            Received traffic
          </Badge>
        )}
      </div>
      {url ? (
        <p className='mt-0.5 font-mono text-xs text-fg-tertiary truncate'>
          {url}
        </p>
      ) : (
        <p className='mt-0.5 text-xs text-fg-tertiary'>URL pending…</p>
      )}
    </ButtonList.Item>
  );
}
